/**
 * Cloud-sync manifest value type, closed item-kind enum, and canonical-bytes
 * encoder used as the Ed25519-signed surface.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/CloudSyncRecord.swift` — the wire-level
 * surface that both platforms can cross-walk. Web doesn't have an iCloud
 * equivalent, so the sync backend is KDS (shared with iOS); the manifest +
 * canonical-bytes shape is the same.
 *
 * THREAT MODEL
 *   - The KDS sync backend sees `(itemKind, manifest fields, opaque blob)`.
 *     It does NOT see plaintext content (sealed under AES-GCM-256 by
 *     `CloudSyncCipher`) and cannot inject a record without the
 *     Ed25519 signing key.
 *   - Canonical bytes sign `SHA256(blob)` not the blob itself, so a 10MB
 *     blob still yields ≤256 bytes of canonical input — keeps Ed25519 fast
 *     even for attachments-worth of contact roster.
 *   - Unit separator 0x1F between variable-length fields (itemKind only,
 *     here) prevents splice attacks. itemKind is enum-bounded so the splice
 *     surface is already tiny, but we still separate for parity with iOS
 *     and to make the wire format trivially extensible if a future kind
 *     adds a sub-id.
 *   - Codec prefix `kaether:cloud-sync:manifest:v1:` is the reserved namespace
 *     for own-device sync over the existing E2EE channel; the canonical bytes
 *     here are NOT prefixed because they're signed bytes, not transport.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs.
 *   Pure data + TextEncoder/DataView only.
 *
 * @module CloudSyncRecord
 */

// ---------------------------------------------------------------------------
// Item kind — closed enum
// ---------------------------------------------------------------------------

/**
 * The closed set of items we will ever cloud-sync. Adding a new kind is
 * intentionally hard: it requires a code-review-visible source change here,
 * which forces every exhaustive switch on `CloudSyncItemKind` elsewhere to
 * fail-to-compile until it handles the new case. There is no "extension
 * point."
 *
 * Mirrors iOS `CloudSyncItemKind.{settings, contactRoster, blockList}`.
 */
export type CloudSyncItemKind = "settings" | "contactRoster" | "blockList";

/** Canonical iteration order for callers that need to enumerate. */
export const CLOUD_SYNC_ITEM_KINDS: readonly CloudSyncItemKind[] = [
  "settings",
  "contactRoster",
  "blockList",
] as const;

/** Type guard for `unknown → CloudSyncItemKind`. */
export function isCloudSyncItemKind(s: unknown): s is CloudSyncItemKind {
  return (
    s === "settings" || s === "contactRoster" || s === "blockList"
  );
}

// ---------------------------------------------------------------------------
// Manifest value type
// ---------------------------------------------------------------------------

/**
 * Manifest envelope for a single cloud-synced item. The signed canonical
 * surface (see `canonicalBytes`) covers exactly these fields, so callers
 * sign the manifest, not the blob, keeping signature cost bounded.
 *
 * Field invariants:
 *   - `itemVersion`     monotonic per-itemKind integer. Drives conflict
 *                       resolution (see `CloudSyncConflictPolicy`).
 *   - `blobSha256`      exactly 32 bytes — SHA-256 of the sealed blob.
 *   - `authorDeviceId`  the deviceId that authored the manifest. Tiebreaker
 *                       in conflict resolution when version + ts both tie.
 *   - `ts`              unix ms. Tiebreaker between equal-version manifests.
 */
export interface CloudSyncManifest {
  readonly itemKind: CloudSyncItemKind;
  readonly itemVersion: number;
  readonly blobSha256: Uint8Array; // 32 bytes
  readonly authorDeviceId: number;
  readonly ts: number;
}

// ---------------------------------------------------------------------------
// Codec / canonical bytes
// ---------------------------------------------------------------------------

/**
 * Reserved namespace prefix for own-device sync over the existing E2EE
 * channel (e.g. a freshly-linked device asking the primary to push the
 * current manifests before the cloud round-trip lands).
 *
 * The canonical signed bytes do NOT include this prefix — they're a
 * deterministic fixed-layout byte sequence (see `canonicalBytes`). The
 * prefix is informational for transport.
 */
export const CLOUD_SYNC_MANIFEST_PREFIX = "kaether:cloud-sync:manifest:v1:";

/** Mapping kind → fixed 1-byte discriminator (stable, must not change). */
function itemKindByte(kind: CloudSyncItemKind): number {
  switch (kind) {
    case "settings":
      return 0x01;
    case "contactRoster":
      return 0x02;
    case "blockList":
      return 0x03;
  }
}

/**
 * Write a 64-bit signed integer (little-endian) at `offset` in `buf`.
 *
 * JavaScript numbers safely cover ±2^53, so we split into two 32-bit
 * halves rather than relying on `BigInt64Array`. Both ts (unix ms) and
 * itemVersion fit comfortably inside that range.
 */
function writeI64LE(buf: Uint8Array, offset: number, value: number): void {
  const negative = value < 0;
  let abs = negative ? -value : value;
  // Split into low32 and high21 (cheap for ≤2^53).
  const low = abs >>> 0;
  abs = Math.floor(abs / 0x100000000);
  const high = abs >>> 0;
  const view = new DataView(buf.buffer, buf.byteOffset + offset, 8);
  if (negative) {
    // Two's complement of 64-bit
    const invLow = (~low + 1) >>> 0;
    const carry = invLow === 0 ? 1 : 0;
    const invHigh = ((~high) + carry) >>> 0;
    view.setUint32(0, invLow, true /* little-endian */);
    view.setUint32(4, invHigh, true);
  } else {
    view.setUint32(0, low, true);
    view.setUint32(4, high, true);
  }
}

/**
 * Compute the canonical signed bytes for a manifest.
 *
 * Layout (total = 1 + 1 + 8 + 1 + 32 + 1 + 4 + 1 + 8 = 57 bytes; well under
 * the 256-byte ceiling that lets us sign 10MB blobs cheaply):
 *
 *   [0]       itemKind discriminator (uint8)
 *   [1]       0x1F unit separator
 *   [2..9]    itemVersion (int64 little-endian)
 *   [10]      0x1F unit separator
 *   [11..42]  blobSha256 (32 bytes)
 *   [43]      0x1F unit separator
 *   [44..47]  authorDeviceId (uint32 little-endian)
 *   [48]      0x1F unit separator
 *   [49..56]  ts (int64 little-endian, unix ms)
 *
 * The 0x1F unit separators between adjacent fields are belt-and-braces —
 * every field here is fixed-width so a splice attack is structurally
 * impossible, but the separators mean a future schema bump that adds
 * variable-length fields cannot accidentally regress to a splice-vulnerable
 * encoding without changing the byte indices.
 *
 * @throws if `m.blobSha256` is not exactly 32 bytes.
 */
export function canonicalManifestBytes(m: CloudSyncManifest): Uint8Array {
  if (m.blobSha256.byteLength !== 32) {
    throw new RangeError(
      `blobSha256 must be 32 bytes (got ${m.blobSha256.byteLength})`,
    );
  }
  const out = new Uint8Array(57);
  let offset = 0;
  out[offset] = itemKindByte(m.itemKind); offset += 1;
  out[offset] = 0x1f; offset += 1;
  writeI64LE(out, offset, m.itemVersion); offset += 8;
  out[offset] = 0x1f; offset += 1;
  out.set(m.blobSha256, offset); offset += 32;
  out[offset] = 0x1f; offset += 1;
  const view = new DataView(out.buffer, out.byteOffset + offset, 4);
  view.setUint32(0, m.authorDeviceId >>> 0, true /* little-endian */);
  offset += 4;
  out[offset] = 0x1f; offset += 1;
  writeI64LE(out, offset, m.ts); offset += 8;
  return out;
}

/**
 * Hard upper bound on canonical manifest size. The 10MB-blob → ≤256-byte
 * canonical-input invariant is what lets us keep Ed25519 cheap; this
 * constant is the ceiling the self-test asserts against.
 */
export const CLOUD_SYNC_CANONICAL_MAX_BYTES = 256 as const;
